import { useRef } from 'react';
import { useAppSelector, useAppDispatch } from '@doda/pc/src/redux/hooks';
import type { ButtonPosition } from '@doda/api-client/aspida/@types';
import {
  appQueryParamsSelector,
  QueryParamsState,
} from '@doda/common/src/redux/features/queryParams';
import {
  createApplyUrl,
  type ReqParamsApplyJob,
} from '@doda/common/src/logics/jobSearchDetail/createApplyUrl';
import { WJOFL_CHANGE } from '@doda/common/src/constants/properties/const';
import {
  BUTTON_POSITION,
  ENTRY_ID,
} from '@doda/common/src/constants/clicksCountUp';
import { tabSelector } from '@doda/pc/src/redux/selectors/queryParams';
import { AppDispatch } from '@doda/pc/src/redux/store';
import type { Tab } from '../../types/tab';
import { clicksCountUp } from '../../services/clicksCountUp';
import { isBot } from '../../utils/isBot';

type Position = 'top' | 'bottom';

/**
 * useApplyのProps
 */
export interface Arg {
  /**
   * ボタン位置
   * * `top` - ボディ上部/追従/ポップアップ
   * * `bottom` - ボディ下部
   */
  position: Position;
  jid: string;
  jobOfferKind: string;
  isScout: boolean;
  initialTab: Tab;
  hasDirectLink: boolean;
  directLink: string | undefined;
  isCasualInterview?: boolean;
  worschrsvid?: string | null;
}

/**
 * 応募ロジック(求人詳細)
 * @param arg
 */
export function useApply(arg: Arg) {
  const dispatch = useAppDispatch();
  const {
    position,
    jid,
    jobOfferKind,
    isScout,
    initialTab,
    hasDirectLink,
    directLink,
    isCasualInterview,
    worschrsvid,
  } = arg;
  const tab = useAppSelector(tabSelector);
  const isApplying = useRef(false);
  const isAgent = jobOfferKind === WJOFL_CHANGE;

  const apply = async () => {
    if (isApplying.current) return;
    isApplying.current = true;
    const activeTab = (tab || initialTab) as Tab;

    if (!isBot()) {
      await clicksCountUp(jid, {
        device: 'PC',
        entryId: isAgent ? ENTRY_ID.support : ENTRY_ID.direct,
        buttonPosition: getButtonPosition(position, activeTab),
      });
    }

    dispatch(
      applyJob({
        isAgent,
        isScout,
        jobOfferKind,
        hasDirectLink,
        directLink,
        jid,
        isCasualInterview,
        worschrsvid,
      })
    );
  };

  return { apply };
}

/**
 * ボタン位置区分生成
 * @param position
 * @param tab
 */
function getButtonPosition(position: Position, tab: Tab): ButtonPosition {
  // 上部/追従/ポップアップ
  if (position === 'top') {
    if (tab === 'jd') return BUTTON_POSITION.requirementTop;
    return BUTTON_POSITION.prTop;
  }
  // 下部
  if (tab === 'jd') return BUTTON_POSITION.requirementBottom;
  return BUTTON_POSITION.prBottom;
}

interface ApplyJobArg extends ReqParamsApplyJob {
  isAgent: boolean;
  isScout: boolean;
}

/**
 * 応募時画面遷移
 * @param arg
 */
function applyJob<S extends { queryParams: QueryParamsState }>(
  arg: ApplyJobArg
) {
  return async (_: AppDispatch, getState: () => S) => {
    const {
      isAgent,
      isScout,
      jobOfferKind,
      hasDirectLink,
      directLink,
      jid,
      isCasualInterview,
      worschrsvid,
    } = arg;
    const {
      bnid,
      osscfl,
      scoutlist_id,
      message_id,
      wlstid,
      record_scoutlist_id,
      record_message_id,
      recommendID,
      KGMmeasures,
    } = appQueryParamsSelector(getState());

    const reqParams: ReqParamsApplyJob = {
      jobOfferKind,
      jobFlag: isAgent ? '0' : '2',
      hasDirectLink,
      directLink,
      fm: 'jobdetail',
      jid,
      bnid,
      osscfl,
      mpsc_sid: '10',
      scoutlist_id,
      message_id,
      wlstid,
      record_scoutlist_id,
      record_message_id,
      scoutKbn: isScout ? '1' : '0',
      recommendID,
      KGMmeasures,
      isCasualInterview,
      worschrsvid,
    };

    const url = createApplyUrl(reqParams);
    window.location.href = url;
  };
}
